import type { TokenModel } from "../../types/authentication";
import type { DashboardSummary, Viewer } from "../../types/viewer";
import type Application from "../../types/application";
import type { JobPosting } from "../../types/jobPosting";
import { httpClient } from "./httpClient";

export interface ProfileUpdateInput {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    countryCode?: string;
}

export const fetchViewerProfile = (): Promise<Viewer> => {
    return httpClient.get<Viewer>("/rest/viewer");
};

export const fetchViewerProfileQuery = () => (): Promise<Viewer> => {
    return fetchViewerProfile();
};

export const fetchDashboardSummary = (): Promise<DashboardSummary> => {
    return httpClient.get<DashboardSummary>("/rest/viewer/dashboard-summary");
};

export const fetchDashboardSummaryQuery = () => (): Promise<DashboardSummary> => {
    return fetchDashboardSummary();
};

export const fetchTodoInterview = (): Promise<{ todoInterview: Application | null }> => {
    return httpClient.get<{ todoInterview: Application | null }>("/rest/viewer/todo-interview");
};

export const fetchTodoInterviewQuery = () => (): Promise<{ todoInterview: Application | null }> => {
    return fetchTodoInterview();
};

export const updateViewerProfile = (input: ProfileUpdateInput): Promise<Viewer> => {
    return httpClient.patch<Viewer>("/rest/viewer/profile", input);
};

export const setViewerLanguage = (language: string): Promise<Viewer> => {
    return httpClient.patch<Viewer>("/rest/viewer/language", { language });
};

export const setViewerNotification = (notification: number): Promise<Viewer> => {
    return httpClient.patch<Viewer>("/rest/viewer/notifications", { notification });
};

export const changeViewerPassword = async (input: {
    oldPassword: string;
    newPassword: string;
}): Promise<TokenModel & { message?: string; type?: string }> => {
    try {
        return await httpClient.post<TokenModel & { message?: string; type?: string }>(
            "/rest/viewer/password/change",
            input,
        );
    } catch (e) {
        if (e instanceof Error && e.message) {
            return { message: e.message } as TokenModel & { message?: string; type?: string };
        }
        throw e;
    }
};

export const linkViewerPassword = async (input: {
    newPassword: string;
}): Promise<TokenModel & { message?: string; type?: string }> => {
    try {
        return await httpClient.post<TokenModel & { message?: string; type?: string }>(
            "/rest/viewer/password/link",
            input,
        );
    } catch (e) {
        if (e instanceof Error && e.message) {
            return { message: e.message } as TokenModel & { message?: string; type?: string };
        }
        throw e;
    }
};

export const postDashboardOpened = (applicationId: string): Promise<{ success: boolean }> => {
    return httpClient.post<{ success: boolean }>("/rest/viewer/dashboard-opened", { applicationId });
};

export const createViewerReuseToken = (jobId: string): Promise<{ token: string; user: string }> => {
    return httpClient.post<{ token: string; user: string }>("/rest/viewer/reuse-token", { jobId });
};

export const fetchRecommendedJobs = (top = 20): Promise<JobPosting[]> => {
    return httpClient.get<JobPosting[]>("/rest/viewer/recommended-jobs", { params: { top } });
};

export const fetchRecommendedJobsQuery = (top?: number) => (): Promise<JobPosting[]> => {
    return fetchRecommendedJobs(top);
};
